import isEmpty from 'lodash/isEmpty';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useCareOptionsQuery } from '~/components/CareTeam/hooks/useCareOptions';
import { useIsMember } from '~/components/Membership/hooks/useIsMember';
import { analyticsTrackEvent } from '~/core/analytics';
import { IN_PERSON_PREMIUM_ENTRYPOINT_VISIBILITY } from '~/core/analytics/events';
import { useNearbyPremiumLocations } from '~/core/dapi/premiumInPerson';
import { useIsPremiumInPersonEnabled } from '~/flags/premiumInPerson';

const REGULAR_SUPPRESSION_REGIONS: string[] = ['NY', 'NJ', 'CA'];

/**
 * Determines which (if any) in-person premium endpoint should be shown in a given context.
 *
 * @returns The type of in-person premium endpoint to show, or null if none should be shown
 */
export default function useShouldShowPremiumInPersonEntrypoint(): {
  shouldShowMemberLearnMore: boolean;
  shouldShowNonMemberLearnMore: boolean;
  shouldShowBookNow: boolean;
} {
  const { data: inPersonCareOptions, isLoading: isLoadingInPersonCareOptions } =
    useCareOptionsQuery('uc');

  const hasExistingInPersonRelationship = !isEmpty(inPersonCareOptions?.data?.book_again);

  const { data: nearbyPremiumInPersonResults, isLoading: isLoadingPremiumInPersonResults } =
    useNearbyPremiumLocations();
  const hasNearbyPremiumInPersonLocation = !isEmpty(nearbyPremiumInPersonResults);

  // This checks if a location ID is shared between the user's existing in-person care and the nearby premium in-person locations.
  // If so, we feel like it's safe to show them in-person premium entrypoints.
  const previousBookingLocationIds =
    inPersonCareOptions?.data?.book_again?.map((loc) => loc.id) ?? [];
  const nearbyPremiumInPersonLocationIds = nearbyPremiumInPersonResults?.map((loc) => loc.id) ?? [];

  const hasInPersonRelationshipWithPremiumProvider = previousBookingLocationIds.some((id) =>
    nearbyPremiumInPersonLocationIds?.includes(id)
  );

  const isLoadingAccount = useSelector((state) => state.account.accountSummaryLoading);
  const isDataLoading =
    isLoadingInPersonCareOptions || isLoadingAccount || isLoadingPremiumInPersonResults;

  // Yeah yeah indexing into the first result to figure out what state's logic to apply is gross
  // But it's our best option atm! We can use geolocation because logic is based on what
  // location's you're near, not where you physically are. We could write something more
  // comprehensive here that looks at all returned states and then applies the strictest logic
  // necessary for the entire subset. But a) our two launch regions are geographically diverse
  // and b) requirements are changing way to dramatically to make it worth the complexity ¯\_(ツ)_/¯.
  const region = nearbyPremiumInPersonResults?.[0]?.state;

  const isPremiumInPersonEnabled = useIsPremiumInPersonEnabled();

  const isMember = useIsMember();

  const shouldShow = useMemo(() => {
    let shouldShowMemberLearnMore = false;
    let shouldShowNonMemberLearnMore = false;
    let shouldShowBookNow = false;

    if (!isPremiumInPersonEnabled || isDataLoading || !region) {
      return { shouldShowMemberLearnMore, shouldShowNonMemberLearnMore, shouldShowBookNow };
    }

    if (hasNearbyPremiumInPersonLocation) {
      if (isMember) {
        shouldShowMemberLearnMore = true;
      } else {
        shouldShowNonMemberLearnMore = true;
      }
    }

    /**
     * These regions are suppressed based on whether the user has
     * a nearby in-person relationship already.
     *
     * This logic only applies to non-members.
     */
    if (REGULAR_SUPPRESSION_REGIONS.includes(region)) {
      if (hasNearbyPremiumInPersonLocation) {
        if (!hasExistingInPersonRelationship || hasInPersonRelationshipWithPremiumProvider) {
          shouldShowBookNow = true;
        }
      }
    } else if (hasNearbyPremiumInPersonLocation) {
      shouldShowBookNow = true;
    }

    return { shouldShowMemberLearnMore, shouldShowNonMemberLearnMore, shouldShowBookNow };
  }, [
    isPremiumInPersonEnabled,
    isDataLoading,
    region,
    isMember,
    hasNearbyPremiumInPersonLocation,
    hasExistingInPersonRelationship,
    hasInPersonRelationshipWithPremiumProvider,
  ]);

  useEffect(() => {
    if (!isDataLoading) {
      analyticsTrackEvent(IN_PERSON_PREMIUM_ENTRYPOINT_VISIBILITY, {
        visible:
          shouldShow.shouldShowBookNow ||
          shouldShow.shouldShowMemberLearnMore ||
          shouldShow.shouldShowNonMemberLearnMore,
        hasExistingInPersonRelationship,
        hasNearbyPremiumInPersonLocation,
        region,
        hasInPersonRelationshipWithPremiumProvider,
        isPremiumInPersonEnabled,
        isExistingSolvPlusMember: isMember,
        nearbyRadiusMiles: 30,
      });
    }
  }, [
    hasExistingInPersonRelationship,
    hasNearbyPremiumInPersonLocation,
    isDataLoading,
    isPremiumInPersonEnabled,
    nearbyPremiumInPersonResults,
    region,
    shouldShow,
    hasInPersonRelationshipWithPremiumProvider,
    isMember,
  ]);

  return shouldShow;
}
